// Bringing a management client on board.
//
// Onboarding is where a property management firm either builds a file it can
// stand behind or spends the next three years asking the owner for things it
// should have collected on day one. The industry answer is a checklist plus a
// welcome packet, and both are held here as rows rather than as a PDF somebody
// has to remember to send — a checklist that lives in a document is a checklist
// nobody completes.
//
// Two things make this more than a to-do list:
//
//   * some items BLOCK. Management cannot lawfully or safely begin without an
//     executed agreement, the client's own trust account, and the Division
//     notification. The page will not let an onboarding go live while any of
//     them are outstanding, and setting one aside demands a stated reason —
//     "we waived it" with no reason is indistinguishable from forgetting.
//   * the trust account is opened FROM here, before any money is received,
//     which is the whole point of an account per client. Opening it raises the
//     1450.750(m) notification and closes its own checklist items in the same
//     action, so the record of it happening is the happening.
//
// The packet tab shows what the owner actually receives: what they signed, what
// is still needed from them, and — the question every new owner asks and every
// management agreement answers worst — when they will hear from us.

import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import {
    PageHeader, Panel, Tabs, Stat, Pill, Table, Field, Modal, Input, Select, Textarea,
    Button, Notice, Loading, Empty, fmtDate, titleCase, money,
} from '../../components/secure/pm/PMKit';

// The order they are worked in, not alphabetical. Agreement first because
// nothing else matters until it is signed; handover last because it is the
// moment management actually begins.
const PHASES = [
    { key: 'agreement', label: 'Agreement' },
    { key: 'client_information', label: 'Client' },
    { key: 'property_information', label: 'Property' },
    { key: 'tenancies', label: 'Tenancies' },
    { key: 'financial_setup', label: 'Financial setup' },
    { key: 'compliance', label: 'Compliance' },
    { key: 'handover', label: 'Handover' },
];

const CADENCES = [
    { key: 'at_onboarding', label: 'When we start' },
    { key: 'monthly', label: 'Every month' },
    { key: 'quarterly', label: 'Every quarter' },
    { key: 'annually', label: 'Every year' },
    { key: 'as_it_happens', label: 'As it happens' },
];

const ITEM_STATUS = [
    { value: 'outstanding', label: 'Outstanding' },
    { value: 'requested', label: 'Requested' },
    { value: 'received', label: 'Received' },
    { value: 'waived', label: 'Waived' },
    { value: 'not_applicable', label: 'Not applicable' },
];

const itemTone = (s) => ({ received: 'good', waived: 'slate', not_applicable: 'slate',
                           requested: 'warn', outstanding: 'bad' }[s] || 'slate');
const done = (s) => ['received', 'waived', 'not_applicable'].includes(s);

const WHO = { owner: 'Client', brokerage: 'Us', tenant: 'Tenant', third_party: 'Third party' };

// A bar rather than a percentage on its own: 12 of 36 reads faster as a shape,
// and the blocking count is what actually decides whether this can go live.
const Progress = ({ total, complete, blocking }) => {
    const pct = total ? Math.round((complete / total) * 100) : 0;
    return (
        <div className="min-w-[9rem]">
            <div className="h-1.5 bg-slate-100 rounded overflow-hidden">
                <div className={`h-full rounded ${blocking ? 'bg-amber-400' : 'bg-emerald-500'}`}
                     style={{ width: `${pct}%` }} />
            </div>
            <div className="text-xs text-slate-500 mt-1 tabular-nums">
                {complete} of {total}
                {blocking > 0 && <span className="text-rose-600 font-semibold"> · {blocking} blocking</span>}
            </div>
        </div>
    );
};

export default function PMOnboardingPage() {
    const apiClient = useApiClient();
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [owners, setOwners] = useState([]);
    const [properties, setProperties] = useState([]);
    const [detail, setDetail] = useState(null);
    const [packet, setPacket] = useState(null);
    const [tab, setTab] = useState('checklist');
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});
    const [note, setNote] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [o, ow, p] = await Promise.all([
                apiClient.get('/api/property-management/onboardings'),
                apiClient.get('/api/property-management/owners'),
                apiClient.get('/api/property-management/properties'),
            ]);
            setRows(o || []); setOwners(ow || []); setProperties(p || []);
        } catch (e) { setNote({ message: e.message }); } finally { setLoading(false); }
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    const openDetail = useCallback(async (id) => {
        setNote(null);
        try {
            const d = await apiClient.get(`/api/property-management/onboardings/${id}`);
            setDetail(d); setPacket(null); setTab('checklist');
        } catch (e) { setNote({ message: e.message }); }
    }, [apiClient]);

    // Every mutation refreshes both the list and the open record, because the
    // blocking count on one is derived from the item statuses on the other.
    const act = async (fn) => {
        setBusy(true); setNote(null);
        try {
            const r = await fn();
            setModal(null); setForm({});
            await load();
            if (detail) await openDetail(detail.onboarding_id);
            if (r?.note) setNote({ tone: 'warn', title: 'Done', message: r.note });
            else if (r?.message) setNote({ tone: 'good', message: r.message });
            return r;
        } catch (e) {
            setNote({
                message: e.message,
                problems: (e.payload?.blocking || []).map((b) =>
                    `${b.item} — from ${WHO[b.from] || b.from}${b.authority ? ` (${b.authority})` : ''}`),
            });
            return null;
        } finally { setBusy(false); }
    };

    const setItem = (item, status) => {
        // Waiving something that blocks needs a reason, so it asks for one rather
        // than letting the save fail with a 422 the user has to decode.
        if (['waived', 'not_applicable'].includes(status) && item.is_blocking) {
            setForm({ item_id: item.item_id, label: item.label, status, waived_reason: '' });
            setModal('waive');
            return;
        }
        act(() => apiClient.put(`/api/property-management/onboarding-items/${item.item_id}`, { status }));
    };

    const loadPacket = async () => {
        setTab('packet');
        if (packet || !detail) return;
        try { setPacket(await apiClient.get(`/api/property-management/onboardings/${detail.onboarding_id}/packet`)); }
        catch (e) { setNote({ message: e.message }); }
    };

    if (loading) return <Loading />;

    const live = rows.filter((r) => r.status !== 'complete' && r.status !== 'abandoned');
    const blocked = live.filter((r) => Number(r.blocking_outstanding) > 0);
    const noAccount = live.filter((r) => !r.escrow_account_id);

    return (
        <div>
            <PageHeader
                title="Client Onboarding"
                subtitle="What we collect before we manage anything, and what the client receives when we do. The trust account is opened here — before the first dollar arrives, which is the point of an account per client."
            >
                <Button onClick={() => { setForm({}); setModal('new'); }}>Start onboarding</Button>
            </PageHeader>

            {note && <Notice {...note} onDismiss={() => setNote(null)} />}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                <Stat label="In progress" value={live.length} />
                <Stat label="Blocked from going live" value={blocked.length}
                      tone={blocked.length ? 'bad' : 'default'}
                      sub={blocked.length ? 'outstanding items that block' : 'nothing blocking'} />
                <Stat label="Without a trust account" value={noAccount.length}
                      tone={noAccount.length ? 'warn' : 'default'}
                      sub="opened before money is received" />
                <Stat label="Live clients" value={rows.filter((r) => r.status === 'complete').length} />
            </div>

            <Panel title="Onboardings"
                   subtitle="Ordered by status, then by when they started.">
                <Table
                    columns={[
                        { key: 'owner_name', label: 'Client',
                          render: (r) => (
                              <div>
                                  <div className="font-medium text-slate-800">{r.owner_name}</div>
                                  <div className="text-xs text-slate-400">{r.property_name || 'no property named yet'}</div>
                              </div>
                          ) },
                        { key: 'status', label: 'Status', nowrap: true,
                          render: (r) => <Pill tone={r.status === 'complete' ? 'good'
                              : r.status === 'abandoned' ? 'slate'
                              : Number(r.blocking_outstanding) ? 'warn' : 'indigo'}>
                              {titleCase(r.status)}</Pill> },
                        { key: 'progress', label: 'Checklist',
                          render: (r) => <Progress total={Number(r.items)} complete={Number(r.done)}
                                                   blocking={Number(r.blocking_outstanding)} /> },
                        { key: 'trust_account', label: 'Trust account',
                          render: (r) => r.trust_account
                              ? <span className="text-sm">{r.trust_account}</span>
                              : <Pill tone="warn">not opened</Pill> },
                        { key: 'started_on', label: 'Started', nowrap: true,
                          render: (r) => fmtDate(r.started_on) },
                    ]}
                    rows={rows.map((r) => ({ ...r, _key: r.onboarding_id }))}
                    onRowClick={(r) => openDetail(r.onboarding_id)}
                    rowTone={(r) => (Number(r.blocking_outstanding) && r.status !== 'complete' ? 'warn' : null)}
                    empty="No client is being onboarded. Start one when a management agreement is signed."
                />
            </Panel>

            {/* ------------------------------------------------------- new */}
            {modal === 'new' && (
                <Modal title="Start onboarding"
                       subtitle="The checklist is built from the template. Naming the property now loads the residential or commercial list instead of both."
                       onClose={() => setModal(null)}>
                    <Select label="Client" value={form.owner_id || ''}
                            onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
                            options={[{ value: '', label: 'Select a client…' },
                                ...owners.map((o) => ({ value: o.owner_id, label: o.legal_name }))]} />
                    <Select label="Property (optional)" value={form.pm_property_id || ''}
                            hint="Residential and commercial collect different things. Loading both and letting people ignore half is how a checklist stops being read."
                            onChange={(e) => setForm({ ...form, pm_property_id: e.target.value })}
                            options={[{ value: '', label: 'Not decided yet — load both lists' },
                                ...properties.map((p) => ({ value: p.pm_property_id, label: p.name }))]} />
                    <Input label="Target live date" type="date" value={form.target_live_date || ''}
                           onChange={(e) => setForm({ ...form, target_live_date: e.target.value })} />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy || !form.owner_id}
                                onClick={() => act(() => apiClient.post('/api/property-management/onboardings', form))}>
                            {busy ? 'Starting…' : 'Start'}
                        </Button>
                    </div>
                </Modal>
            )}

            {/* ----------------------------------------------------- waive */}
            {modal === 'waive' && (
                <Modal title="Set aside a blocking item"
                       subtitle={form.label}
                       onClose={() => { setModal(null); setForm({}); }}>
                    <Notice tone="warn" title="This item blocks going live"
                            message="Say why it is being set aside. Without a reason there is no way to tell later whether it was a decision or an oversight — and this is the record an examiner reads." />
                    <Textarea label="Reason" rows={3} value={form.waived_reason || ''}
                              onChange={(e) => setForm({ ...form, waived_reason: e.target.value })} />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => { setModal(null); setForm({}); }}>Cancel</Button>
                        <Button variant="danger" disabled={busy || !form.waived_reason?.trim()}
                                onClick={() => act(() => apiClient.put(
                                    `/api/property-management/onboarding-items/${form.item_id}`,
                                    { status: form.status, waived_reason: form.waived_reason }))}>
                            {busy ? 'Saving…' : 'Set aside'}
                        </Button>
                    </div>
                </Modal>
            )}

            {/* --------------------------------------------- open account */}
            {modal === 'account' && detail && (
                <Modal title="Open this client’s trust account"
                       subtitle={`${detail.owner_name} — an account of their own, separate from every other client and from the brokerage’s money.`}
                       onClose={() => setModal(null)}>
                    <Input label="Bank" value={form.bank_name || ''}
                           onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
                    <Input label="Bank address" value={form.bank_address || ''}
                           onChange={(e) => setForm({ ...form, bank_address: e.target.value })} />
                    <Input label="Account number" value={form.account_number || ''}
                           hint="Leave blank if the bank has not issued it yet. The account then opens as PENDING, shows as a gap on the master escrow log, and no client money should be received into it until the number is entered."
                           onChange={(e) => setForm({ ...form, account_number: e.target.value })} />
                    <Notice tone="warn" title="This raises the Division notification"
                            message="68 Ill. Adm. Code 1450.750(m) — the Division must be told about a new special account within 10 days. Opening it here creates that obligation with its due date, assigns the client to the account, and closes the checklist items it satisfies." />
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy || !form.bank_name}
                                onClick={() => act(() => apiClient.post(
                                    `/api/property-management/onboardings/${detail.onboarding_id}/open-trust-account`,
                                    form))}>
                            {busy ? 'Opening…' : 'Open account'}
                        </Button>
                    </div>
                </Modal>
            )}

            {/* ---------------------------------------------------- detail */}
            {detail && (
                <Modal wide title={detail.owner_name}
                       subtitle={[detail.property_name, titleCase(detail.status)].filter(Boolean).join(' · ')}
                       onClose={() => { setDetail(null); setPacket(null); }}>
                    <Tabs active={tab} onChange={(t) => (t === 'packet' ? loadPacket() : setTab(t))}
                          tabs={[{ key: 'checklist', label: `Checklist (${detail.progress.outstanding} outstanding)` },
                                 { key: 'packet', label: 'Client packet' }]} />

                    {tab === 'checklist' && (
                        <>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 my-4">
                                <Stat label="Collected" value={`${detail.progress.done}/${detail.progress.total}`} />
                                <Stat label="Blocking" value={detail.progress.blocking}
                                      tone={detail.progress.blocking ? 'bad' : 'good'}
                                      sub={detail.can_go_live ? 'clear to go live' : 'management cannot begin'} />
                                <Stat label="Trust account"
                                      value={detail.trust_account ? (detail.account_status || 'open') : 'none'}
                                      tone={detail.trust_account
                                          ? (detail.account_status === 'pending' ? 'warn' : 'good') : 'bad'}
                                      sub={detail.bank_name || 'opened before money is received'} />
                                <Stat label="W-9" value={detail.w9_on_file ? 'on file' : 'missing'}
                                      tone={detail.w9_on_file ? 'good' : 'warn'}
                                      sub="needed before the first distribution" />
                            </div>

                            <div className="flex flex-wrap gap-2 mb-4">
                                {!detail.escrow_account_id && (
                                    <Button onClick={() => { setForm({}); setModal('account'); }}>
                                        Open the client’s trust account
                                    </Button>
                                )}
                                <Button variant={detail.can_go_live ? 'primary' : 'ghost'}
                                        disabled={busy || detail.status === 'complete'}
                                        onClick={() => act(() => apiClient.post(
                                            `/api/property-management/onboardings/${detail.onboarding_id}/complete`, {}))}>
                                    {detail.status === 'complete' ? 'Live' : 'Go live'}
                                </Button>
                            </div>

                            {!detail.can_go_live && (
                                <Notice tone="warn" title="Not ready to go live"
                                        message="These block management beginning. Each can be received, or set aside with a stated reason."
                                        problems={detail.blocking_items.map((b) =>
                                            `${b.label} — from ${WHO[b.responsibility] || b.responsibility}${b.authority ? ` (${b.authority})` : ''}`)} />
                            )}

                            {PHASES.filter((p) => (detail.items_by_phase[p.key] || []).length).map((p) => {
                                const items = detail.items_by_phase[p.key];
                                const left = items.filter((i) => !done(i.status)).length;
                                return (
                                    <Panel key={p.key} className="mt-4" title={p.label}
                                           subtitle={left ? `${left} outstanding` : 'complete'}>
                                        <Table
                                            columns={[
                                                { key: 'label', label: 'Item',
                                                  render: (i) => (
                                                      <div>
                                                          <div className="font-medium text-slate-800">
                                                              {i.label}
                                                              {!!i.is_blocking && <span className="ml-2"><Pill tone="bad">blocks</Pill></span>}
                                                          </div>
                                                          {i.detail && <div className="text-xs text-slate-500 mt-0.5 max-w-xl">{i.detail}</div>}
                                                          {i.authority && <div className="text-xs text-slate-400 mt-0.5">{i.authority}</div>}
                                                          {i.waived_reason && (
                                                              <div className="text-xs text-amber-700 mt-1">
                                                                  Set aside: {i.waived_reason}
                                                              </div>
                                                          )}
                                                      </div>
                                                  ) },
                                                { key: 'responsibility', label: 'From', nowrap: true,
                                                  render: (i) => <Pill tone={i.responsibility === 'owner' ? 'indigo' : 'slate'}>
                                                      {WHO[i.responsibility] || i.responsibility}</Pill> },
                                                { key: 'received_on', label: 'Received', nowrap: true,
                                                  render: (i) => fmtDate(i.received_on) },
                                                { key: 'status', label: 'Status', align: 'right',
                                                  render: (i) => (
                                                      <div className="flex items-center justify-end gap-2">
                                                          <Pill tone={itemTone(i.status)}>{titleCase(i.status)}</Pill>
                                                          {/* Bare select, not the kit's: that one carries a
                                                              label span, and an empty one pushes the control
                                                              off the row it belongs to. */}
                                                          <select value={i.status}
                                                                  onChange={(e) => setItem(i, e.target.value)}
                                                                  className="w-36 p-1.5 border border-slate-300 rounded text-xs bg-white
                                                                             focus:outline-none focus:ring-2 focus:ring-slate-500">
                                                              {ITEM_STATUS.map((o) => (
                                                                  <option key={o.value} value={o.value}>{o.label}</option>
                                                              ))}
                                                          </select>
                                                      </div>
                                                  ) },
                                            ]}
                                            rows={items.map((i) => ({ ...i, _key: i.item_id }))}
                                            rowTone={(i) => (i.is_blocking && !done(i.status) ? 'warn' : null)}
                                        />
                                    </Panel>
                                );
                            })}
                        </>
                    )}

                    {tab === 'packet' && (
                        !packet ? <Loading /> : (
                            <div className="mt-4">
                                <Panel title={packet.title}
                                       subtitle="What the client receives. Generated from what the brokerage actually does, so a promise made here can be checked against the compliance calendar later.">
                                    <dl>
                                        <Field label="Client" value={packet.client?.name} />
                                        <Field label="Contact" value={[packet.client?.contact, packet.client?.email].filter(Boolean).join(' · ')} />
                                        <Field label="Property" value={packet.property
                                            ? `${packet.property.name} — ${packet.property.address}` : null} />
                                        <Field label="Statements delivered by" value={titleCase(packet.statements_delivered_by || '')} />
                                    </dl>
                                </Panel>

                                <Panel className="mt-4" title="What you signed">
                                    {packet.what_you_signed ? (
                                        <dl className="grid sm:grid-cols-2 gap-x-6">
                                            <Field label="Form" value={titleCase(packet.what_you_signed.form)} />
                                            <Field label="Effective" value={fmtDate(packet.what_you_signed.effective)} />
                                            <Field label="Distributions" value={packet.what_you_signed.distribution_day
                                                ? `Around the ${packet.what_you_signed.distribution_day} of each month` : null} />
                                            <Field label="Reserve held" value={money(packet.what_you_signed.reserve_floor)} />
                                            <Field label="We may spend without asking" value={money(packet.what_you_signed.spend_limit_without_approval)}
                                                   hint="Above this we come to you first." />
                                            <Field label="Emergency limit" value={money(packet.what_you_signed.emergency_limit)}
                                                   hint="Only to stop damage or make the building safe." />
                                        </dl>
                                    ) : (
                                        <Empty title="No active agreement"
                                               message="The packet fills in from the executed management agreement. Nothing here until it is signed." />
                                    )}
                                </Panel>

                                <Panel className="mt-4" title="Your money"
                                       subtitle={packet.your_trust_account?.note}>
                                    <dl>
                                        <Field label="Account" value={packet.your_trust_account?.name} />
                                        <Field label="Bank" value={packet.your_trust_account?.bank} />
                                        <Field label="Status" value={packet.your_trust_account?.status
                                            ? titleCase(packet.your_trust_account.status) : null} />
                                    </dl>
                                </Panel>

                                <Panel className="mt-4" title="Still needed from you"
                                       subtitle={`${(packet.still_needed_from_you || []).length} item(s)`}>
                                    {(packet.still_needed_from_you || []).length === 0 ? (
                                        <Empty title="Nothing outstanding"
                                               message="Everything the client owes us has been collected." />
                                    ) : (
                                        <ul className="text-sm space-y-2">
                                            {packet.still_needed_from_you.map((s, i) => (
                                                <li key={i}>
                                                    <span className="font-medium text-slate-800">{s.label}</span>
                                                    {s.detail && <span className="block text-xs text-slate-500">{s.detail}</span>}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </Panel>

                                <Panel className="mt-4" title="When you will hear from us"
                                       subtitle="The question every new owner asks and the one a management agreement answers least well.">
                                    {CADENCES.filter((c) => (packet.what_to_expect?.[c.key] || []).length).map((c) => (
                                        <div key={c.key} className="mb-4 last:mb-0">
                                            <h4 className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">
                                                {c.label}
                                            </h4>
                                            <ul className="space-y-2">
                                                {packet.what_to_expect[c.key].map((e) => (
                                                    <li key={e.expectation_id} className="text-sm">
                                                        <span className="font-medium text-slate-800">{e.label}</span>
                                                        {e.timing && <span className="text-slate-500"> — {e.timing}</span>}
                                                        {e.detail && <span className="block text-xs text-slate-500">{e.detail}</span>}
                                                        {e.authority && <span className="block text-xs text-slate-400">{e.authority}</span>}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </Panel>
                            </div>
                        )
                    )}
                </Modal>
            )}
        </div>
    );
}
